import { GetStaticPaths, GetStaticProps } from "next";
import NextImage from "next/image";
import Link from "next/link";
import type {
  CategoryDetails,
  ContentBlock,
  Image as ContentImage,
  SubCategory,
} from "@/types";
import { BUSINESS_NAME, contactDetails } from "@/content";
import {
  getServiceCategories,
  getServiceCategoryBySlug,
} from "@/content/services";
import ContentArticleReader from "@/ui/content-article-reader/content-article-reader";
import {PageSeo} from '@modes0/ui-components-seo';
import {seoSiteProfile} from '@/content/seo';
import {
  Breadcrumbs as Modes0Breadcrumbs,
  PageWithSidebarLayout,
  ServiceCategorySidebarNavigator,
  ServicesImageCards,
  ServicesRow,
  type Crumb,
  type ServiceCategorySidebarNavigatorCategory,
  type ServiceCategorySidebarNavigatorService,
  type ServicesImageCardsTile,
  type ServicesRowTile,
} from "@modes0/ui-components";

type ServiceCategoryPageProps = {
  categorySlug: string;
};

const brandColor = "#28aae1";
const brandDarkColor = "#15171e";
const categoryAccentColours: Record<string, string> = {
  residential: brandColor,
  commercial: "#28aae1",
};
const fallbackAccentColours = [brandColor, "#28aae1", "#15171e"];

const getImageSrc = (image?: Partial<ContentImage>) =>
  image?.url || image?.original || image?.thumbnail || "";

const getPhoneHref = (phone?: string) =>
  phone ? `tel:${phone.replace(/\s+/g, "")}` : "/contact";

const getCategoryHref = (category: CategoryDetails) =>
  `/${category.categoryType}/${category.slug}`;

const getCategoryDescription = (category: CategoryDetails) =>
  category.description?.filter(Boolean).join(" ") || "";

const getCategoryAccentColour = (category: CategoryDetails, index: number) =>
  categoryAccentColours[category.key] ||
  fallbackAccentColours[index % fallbackAccentColours.length];

const getServiceHref = (
  service: SubCategory,
  category = service.headCategory,
) =>
  service.link || `/${category.categoryType}/${category.slug}/${service.slug}`;

const getContentBlocks = (serviceCategory: CategoryDetails): ContentBlock[] => {
  if (serviceCategory.content?.length) {
    return serviceCategory.content;
  }

  return [
    {
      id: 1,
      title: `${serviceCategory.name} in Sydney`,
      content: serviceCategory.description?.[0] || serviceCategory.long_name,
      points: serviceCategory.points
        ?.map((point) => point.title)
        .filter(Boolean),
    },
  ];
};

const getCategoryCrumbs = (category: CategoryDetails): Crumb[] => [
  { title: "Home", href: "/" },
  { title: "Services", href: `/${category.categoryType}` },
  { title: category.name },
];

const getCategoryImage = (category: CategoryDetails): ContentImage | null => {
  const image = category.images?.[0] as Partial<ContentImage> | undefined;
  const src = getImageSrc(image) || category.heroImage?.url || "";

  if (!src) {
    return null;
  }

  const title =
    image?.title ||
    image?.originalTitle ||
    image?.thumbnailTitle ||
    category.heroImage?.title ||
    category.name;
  const alt =
    image?.alt ||
    image?.originalAlt ||
    image?.thumbnailAlt ||
    category.heroImage?.alt ||
    title;

  return {
    ...image,
    id: image?.id || category.heroImage?.id || category.id,
    url: image?.url || src,
    original: image?.original || src,
    thumbnail: image?.thumbnail || src,
    title,
    alt,
    originalAlt: image?.originalAlt || alt,
    thumbnailAlt: image?.thumbnailAlt || alt,
    thumbnailTitle: image?.thumbnailTitle || title,
  } as ContentImage;
};

const getServiceImage = (service: SubCategory) => {
  const image = service.images?.[0] as Partial<ContentImage> | undefined;
  const src = getImageSrc(image) || service.heroImage?.url || "";

  if (!src) {
    return undefined;
  }

  const title =
    image?.title ||
    image?.originalTitle ||
    image?.thumbnailTitle ||
    service.heroImage?.title ||
    service.title;
  const alt =
    image?.alt ||
    image?.originalAlt ||
    image?.thumbnailAlt ||
    service.heroImage?.alt ||
    title;

  return {
    src,
    url: src,
    original: image?.original || src,
    thumbnail: image?.thumbnail || src,
    alt,
    title,
    originalAlt: image?.originalAlt || alt,
    thumbnailAlt: image?.thumbnailAlt || alt,
  };
};

const getServiceNavigatorItems = (
  category: CategoryDetails,
  accentColor: string,
): ServiceCategorySidebarNavigatorService[] =>
  (category.subCategories || []).map((service) => ({
    id: `${category.id}-${service.id}`,
    title: service.title,
    description: service.description,
    href: getServiceHref(service, category),
    accentColor,
  }));

const getServiceNavigatorCategories = (
  activeCategory: CategoryDetails,
): ServiceCategorySidebarNavigatorCategory[] =>
  getServiceCategories().map((category, index) => {
    const accentColor =
      category.id === activeCategory.id
        ? brandColor
        : getCategoryAccentColour(category, index);

    return {
      id: category.id,
      title: category.name,
      description: getCategoryDescription(category),
      href: getCategoryHref(category),
      services: getServiceNavigatorItems(category, accentColor),
      accentColor,
    };
  });

const getServiceTiles = (serviceCategory: CategoryDetails): ServicesRowTile[] =>
  (serviceCategory.subCategories || []).map((service) => ({
    id: `${serviceCategory.id}-${service.id}`,
    title: service.title,
    description: service.description,
    href: getServiceHref(service, serviceCategory),
    image: getServiceImage(service),
    accentColor: brandColor,
    accentDarkColor: brandDarkColor,
    ctaLabel: "View service",
  }));

const getImageCardTiles = (
  serviceCategory: CategoryDetails,
): ServicesImageCardsTile[] =>
  (serviceCategory.images || [])
    .slice(0, 6)
    .map((image, index) => {
      const src = getImageSrc(image as Partial<ContentImage>);
      const title =
        image.title ||
        image.originalTitle ||
        `${serviceCategory.name} ${index + 1}`;
      const alt = image.alt || image.originalAlt || image.thumbnailAlt || title;

      return {
        id: `${serviceCategory.id}-image-${index}`,
        title,
        description: index === 0 ? serviceCategory.description?.[0] : undefined,
        href: getCategoryHref(serviceCategory),
        image: {
          src,
          url: src,
          original: image.original || src,
          thumbnail: image.thumbnail || src,
          alt,
          title,
        },
        accentColor: brandColor,
        accentDarkColor: brandDarkColor,
      };
    })
    .filter((tile) => Boolean(tile.image?.src));

const ServiceCategorySidebar = ({
  serviceCategory,
}: {
  serviceCategory: CategoryDetails;
}) => (
  <ServiceCategorySidebarNavigator
    key={serviceCategory.slug}
    categories={getServiceNavigatorCategories(serviceCategory)}
    Link={Link}
    title="Browse services"
    description={`Explore ${serviceCategory.name.toLowerCase()} and related security services from ${BUSINESS_NAME}.`}
    navTitle="Security service categories"
    ctaLabel={contactDetails.contactNumberOne || "Contact us"}
    ctaHref={getPhoneHref(contactDetails.contactNumberOne)}
    ctaAriaLabel={`Contact ${BUSINESS_NAME} about ${serviceCategory.name}`}
    supportText="Need help choosing the right service?"
    supportLinkLabel="Contact us"
    supportLinkHref="/contact"
    supportLinkAriaLabel={`Contact ${BUSINESS_NAME}`}
    supportTextSuffix=" and we will point you in the right direction."
    itemCtaLabel="View service"
    categoryCtaLabel="View category"
    searchPlaceholder="Search services"
    searchAriaLabel="Search security services"
    defaultOpenCategories={[serviceCategory.id]}
    showCategoryDescriptions
    showServiceIcons
    categoryIconToken="layers"
    serviceIconToken="wrench"
    accentColor={categoryAccentColours[serviceCategory.key] || brandColor}
    backgroundColor="#ffffff"
    surfaceColor="#ffffff"
    serviceSurfaceColor="#f4f8ff"
    textColor="#151515"
    borderColor="rgba(199, 137, 33, 0.18)"
    maxWidth="100%"
    ariaLabel={`${serviceCategory.name} service navigation`}
  />
);

const ServiceCategoryContent = ({
  serviceCategory,
}: {
  serviceCategory: CategoryDetails;
}) => {
  const categoryImage = getCategoryImage(serviceCategory);
  const contentBlocks = getContentBlocks(serviceCategory);
  const categoryDescription =
    serviceCategory.description?.[0] || serviceCategory.long_name;

  return (
    <section className="w-full bg-[#f5f8fb] py-10 md:py-14">
      <PageWithSidebarLayout
        maxWidth="xl"
        gridTemplateColumns={{
          lg: "2fr 1fr",
          xl: "2fr 1fr",
        }}
        main={
          <div className="min-w-0 bg-white px-4 py-5 md:px-6 md:py-6">
            <div className="mb-6">
              <Modes0Breadcrumbs
                crumbs={getCategoryCrumbs(serviceCategory)}
                homeAccent={brandColor}
                currentPageAccent={brandColor}
              />
            </div>
            <article className="min-w-0">
              <header className="mx-auto max-w-[720px] text-center">
                <p className="m-0 text-sm font-black uppercase tracking-normal text-[#28aae1]">
                  {BUSINESS_NAME}
                </p>
                <h1 className="m-0 mt-3 text-balance text-3xl font-black leading-tight tracking-normal text-[#050505] md:text-[42px] md:leading-[1.06]">
                  {serviceCategory.name}
                </h1>
                {categoryDescription ? (
                  <p className="mx-auto mt-4 max-w-[650px] text-pretty text-base font-semibold leading-7 text-[#4a4a4a] md:text-lg md:leading-8">
                    {categoryDescription}
                  </p>
                ) : null}
              </header>
              <div className="mt-6">
                <ContentArticleReader contentBlocks={contentBlocks} />
              </div>
              {categoryImage && (
                <figure className="mt-8 overflow-hidden rounded-lg border border-black/10 bg-white shadow-sm">
                  <div className="relative h-[260px] w-full md:h-[420px]">
                    <NextImage
                      src={categoryImage.url}
                      alt={categoryImage.alt || serviceCategory.name}
                      fill
                      sizes="(min-width: 1024px) 820px, calc(100vw - 32px)"
                      className="object-cover"
                      loading="lazy"
                    />
                  </div>
                  <figcaption className="border-t border-black/10 px-4 py-3 text-sm leading-6 text-neutral-700">
                    {categoryImage.caption ||
                      categoryImage.description ||
                      categoryImage.title ||
                      `${serviceCategory.name} services by ${BUSINESS_NAME}.`}
                  </figcaption>
                </figure>
              )}
            </article>
          </div>
        }
        sidebar={<ServiceCategorySidebar serviceCategory={serviceCategory} />}
      />
    </section>
  );
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: getServiceCategories().map((category) => ({
    params: { categorySlug: category.slug },
  })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<ServiceCategoryPageProps> = ({
  params,
}) => ({
  props: {
    categorySlug: String(params?.categorySlug || ""),
  },
});

export default function ServiceCategoryPage({
  categorySlug,
}: ServiceCategoryPageProps) {
  const serviceCategory = getServiceCategoryBySlug(categorySlug);

  if (!serviceCategory) {
    return null;
  }

  return (
    <>
      <PageSeo meta={serviceCategory.metaTags} siteProfile={seoSiteProfile} includeBusinessSchema />
      <ServiceCategoryContent serviceCategory={serviceCategory} />
      <section className="bg-white py-12 md:py-16">
        <ServicesRow
          items={getServiceTiles(serviceCategory)}
          Link={Link}
          overline={BUSINESS_NAME}
          title={`${serviceCategory.name} services`}
          description={serviceCategory.description?.[0]}
          tileCtaLabel="View service"
          visibleTileCount={3}
          accentColor={brandColor}
          ariaLabel={`${serviceCategory.name} services`}
        />
      </section>
      <section className="bg-[#f5f8fb] py-12 md:py-16">
        <ServicesImageCards
          items={getImageCardTiles(serviceCategory)}
          Link={Link}
          overline={`${BUSINESS_NAME} services`}
          title={`${serviceCategory.name} examples`}
          description={serviceCategory.description?.[0]}
          tileCtaLabel="View category"
          accentColor={brandColor}
          backgroundColor="#f5f8fb"
          imageHeight={260}
          ariaLabel={`${serviceCategory.name} imagery`}
        />
      </section>
    </>
  );
}
